import { useEffect } from 'react';
import { Column, useFilters, usePagination, useSortBy, useTable } from 'react-table';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  getCurrentPage,
  getTotalPages,
  setTableCurrentPage,
  setTableSize,
  setTableSorting,
} from 'redux/slices/tableConfig';

import { SortItem } from 'components/Table/types';

const useTableComponent = (
  columns: readonly Column<{}>[],
  data: readonly {}[],
  defaultSortItems?: SortItem[],
  hasApiSorting?: boolean
) => {
  const dispatch = useAppDispatch();
  const totalPages = useAppSelector(getTotalPages);
  const currentPage = useAppSelector(getCurrentPage);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: defaultSortItems,
      },
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      autoResetFilters: false,
      autoResetSortBy: false,
      manualSortBy: hasApiSorting,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    allColumns,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageCount,
    state: { pageIndex, pageSize, sortBy },
  } = tableInstance;

  useEffect(() => {
    gotoPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    dispatch(setTableCurrentPage(pageIndex));
  }, [pageIndex]);

  useEffect(() => {
    dispatch(setTableSize(pageSize));
  }, [pageSize]);

  useEffect(() => {
    if (sortBy.length) dispatch(setTableSorting(sortBy[0]));
  }, [sortBy]);

  return {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    allColumns,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageCount,
    pageSize,
    pageIndex,
  };
};

export { useTableComponent };
